import type { MethodologyCopy } from "./types";

/**
 * English copy for `/methodology`.
 *
 * Nothing here states a threshold. Every number on the rendered page is read
 * from `lib/face/metrics.ts` at render time — see `types.ts` for why that is a
 * rule rather than a convenience.
 */
export const en: MethodologyCopy = {
  title: "How Fazly Scores a Face — Every Threshold, Published | Fazly",
  description:
    "The complete scoring method: what happens to your photo, the eight measurements, the target and tolerance behind each one, the weights, the band cutoffs and the six face-shape rules.",
  h1: "How Fazly scores a face",
  standfirst:
    "Every threshold, every tolerance, every weight. Read from the same constants the scorer runs on.",
  answer:
    "Fazly scores a face from eight measurements taken off a 478-point landmark mesh that runs in your browser. Each measurement is compared to a stated target, and the score falls linearly from 100 at the target to 0 at a stated tolerance — no curve, no bell, nothing that flatters a number near the middle. The eight scores are then combined by fixed weights into a composite out of 100, and the composite is labelled with one of four bands. This page publishes all of it: every target, every tolerance, every weight, the band cutoffs, and the three-term rules behind each of the six face shapes. The numbers are read at render time from the same constants the scorer uses, so this page cannot describe a version of the arithmetic that is not running. None of it is a finding about attractiveness, and the last section says exactly what it is not.",

  pipelineHeading: "What happens to your photograph",
  pipeline: [
    {
      title: "The model loads before you choose anything",
      body: "MediaPipe's FaceLandmarker is fetched as WebAssembly and warmed during idle time, so the file is already in memory when you pick a photo. It is about 16 MB, cached by the browser after the first visit.",
    },
    {
      title: "The image is decoded in the page",
      body: "It is read with the EXIF orientation applied and drawn to a canvas at a maximum edge of 1024 pixels. Nothing is uploaded: there is no endpoint that accepts an image, which you can confirm in your browser's network tab while you use any tool on this site.",
    },
    {
      title: "478 landmarks come back, and get straightened",
      body: "The mesh returns normalized coordinates, which are scaled to the image's real pixel dimensions and then de-rolled — rotated so the eye line is horizontal — because every angle below is measured against the frame rather than against the head.",
    },
    {
      title: "Eight measurements, then one composite",
      body: "Each measurement is a distance, an angle, or a ratio between two of them. Each is scored against a target, weighted, and summed. Every number in that chain is in the tables below.",
    },
  ],

  scoringHeading: "The eight measurements and their thresholds",
  scoringBody:
    "The target is the value that scores 100. The tolerance is the distance from the target at which the score reaches 0. Where a measurement is scored on two rules — jawline is the only one — the published score is the mean of both.",
  scoringNote:
    "The falloff is linear, deliberately. A bell curve or an exponential looks more considered and behaves worse at the two places anyone actually looks: near the target it is almost flat, so two visibly different faces get the same score, and far out in the tail it flattens again, so a wildly off measurement and a slightly off one both read the same. A straight line spends its resolution evenly and the number moves when the face moves.",

  weightsHeading: "How the eight become one",
  weightsBody:
    "The composite is the weighted sum of the eight scores. Symmetry carries the most because it is the measurement with the least contested link to how faces are actually rated, and because it is the one this pipeline measures most reliably: it compares mirrored pairs of points against the face's own midline rather than comparing a ratio to an invented target. The weights are a judgement. They are not derived from a dataset, and no honest set of weights in this category is.",

  bandsHeading: "The four bands",
  bandsBody:
    "A composite is labelled by which floor it clears. The cutoffs are round numbers chosen to be legible, not thresholds discovered in data — there is no data in which they could be discovered.",
  bandNames: {
    strong: "Strong",
    good: "Good",
    fair: "Within range",
    weak: "Below range",
  },

  shapesHeading: "The six face-shape rules",
  shapesBody:
    "Each shape is three conditions that must hold together, and the three are combined by geometric mean rather than by adding them. That difference is not cosmetic: a sum lets a face win a shape on two terms while failing the one that defines it, and a deliberately short wide test face scored 'oblong' that way, because its jaw and forehead ratios matched an oblong face exactly and the zero it earned on 'longer than wide' was simply outvoted. A geometric mean makes a zero on any defining term fatal, which is what AND means. The shape reported is the highest-scoring rule, and the confidence figure is how far clear it finished from the runner-up — not a probability.",

  limitsHeading: "What none of this tells you",
  limits: [
    "There is no ground truth for facial attractiveness, so no score here — or anywhere in this category — can be validated against it. Every target below is a convention somebody chose, including ours.",
    "The symmetry figure is dominated by head rotation. A few degrees of turn moves it more than most real asymmetry does, which is why the tool asks for a straight-on photograph and why comparing two symmetry readings from different photos tells you about the photos.",
    "The landmark model has its own error, and it is larger at the jaw and the hairline than at the eyes. A gonial angle is a less repeatable number than an intercanthal distance, and the scores do not say so.",
    "The six face shapes were invented to sell haircuts. They are not a finding in any anatomical study, faces sit on a continuum, and a face near a boundary will genuinely get different answers from different tools and from different photographs.",
    "φ has no established relationship to facial attractiveness. It is here because this vocabulary is built on it and a tool that refused to compute it would be answering a different question than the one people arrive with.",
    "fWHR is the one measurement here with a real peer-reviewed literature behind it, and that literature is about dominance perception rather than attractiveness. It is also contested.",
  ],

  columns: {
    measurement: "Measurement",
    quantity: "What is measured",
    target: "Target",
    tolerance: "Tolerance",
    weight: "Weight",
    shape: "Shape",
  },

  sourceHeading: "Where these numbers live",
  sourceBody:
    "Every figure on this page is read at render time from the constants the scorer itself uses — SCORING, SHAPE_RULES, COMPOSITE_WEIGHTS and BAND_FLOORS in the face metrics module. There is no second copy of any threshold in this page's text, which is why this page cannot describe a version of the arithmetic that is not the one running on your photograph.",

  faqHeading: "Questions",

  faq: [
    {
      q: "Where do the targets come from?",
      a: "Three sources, and they are not equally strong. Two ratios are anchored on published anthropometric norms; fWHR's target is a population mean from its own literature; the rest are set from the measured spread across test photographs, which means they describe what these measurements actually do rather than what they ought to be.",
    },
    {
      q: "Why publish the thresholds at all?",
      a: "Because a score you cannot check is a slot machine, and every other tool in this category is one. Publishing the arithmetic invites the argument, and the argument is the product: a reader who can see that their face missed 'oval' by 0.03 has learned something, and a reader given only the word has not.",
    },
    {
      q: "Can I reproduce these numbers myself?",
      a: "Yes, and that is the point. Every quantity in the table is two identifiable points on a face and one division. A photograph, a protractor and an hour will get you close enough to tell whether we are measuring what we say we are.",
    },
  ],

  tryFirst: "Now go and disagree with a number:",
  toolsCta: "open the free tools",
};
